import { START_MONTH, START_YEAR, MAX_FACILITY_FEE_INSTALLMENTS } from '../constants';
import {
  buildOldCohortRevenueRows,
  mapOldCohortRevenue,
} from './mapOldCohortRevenue';
import {
  buildCohortRepaymentSpecs,
  estimateMaxRepaymentMonth,
  resolveMonthlyFeePctFromState,
  simulateTwelveBalance,
} from './simulateTwelveBalance';
import { computeFacilityLimitViolations } from './facilityLimitViolations';
import {
  buildMaxExposureByOutputMonth,
  buildMaxMonthlyDrawByOutputMonth,
  resolveFacilityFeeAmount,
  resolveRequiredMaxExposure,
  shouldApplyDrawLimits,
} from './resolvePlanCovenantLimits';
import { calcIRR } from './calcIRR';
import { resultMonthLabel } from './monthLabel';
import { isResultsCalendarAligned, resolvePlanStartMonth } from './planStartMonth';
import {
  calcExposurePctByMonth,
  calcTwelvePerspectiveMetrics,
} from './twelvePerspective';
import {
  calcRunoffByFundedCohortWindow,
  parseRunoffExposureCohortCount,
} from './calcRunoffByFundedCohortWindow';
import {
  fitHillRoasModel,
  marginalFromCumulativeVector,
  predictRoasVectorPct,
} from './roasSaturation';
import { resolveLatestCohortMarketingSpend } from './uaPrediction';
import { resolveEffectiveRepaymentPeriods } from './resolveEffectiveRepaymentPeriods';
import { resolveAllCohortRepaymentRoasVectors } from './extendRoasVectorForRepayment';
import { makeRoasScenarioAdjuster } from './applyRoasScenarioAdjustment';
import { resolveFacilityFeeStartMonth } from './resolveDealIrrModelInputs';
import { resolveUseHillRoasForFunding } from './resolveUseHillRoasForFunding';
import { buildHillSpendRoasVectors } from './applyHillRoasEfficiencyRatio';
import { applyCommitmentToPlanArrays } from './resolveFundingCommitment';
import {
  resolveCohortDraw,
  resolveEffectiveRevSharePct,
} from './resolvePlanFunding';

/** Last cohort row marketing spend (cash payment lands in plan month 0). */
export function resolveLastCohortMarketingPayment(state) {
  const override = Number(state?.lastMarketingSpend);
  if (override > 0) {
    return override;
  }
  return resolveLatestCohortMarketingSpend(state?.cohortSheet) ?? 0;
}

/** Last plan row index with marketing spend, else last row. */
export function lastMarketingMonthIndex(marketing) {
  if (!marketing?.length) {
    return 0;
  }
  for (let i = marketing.length - 1; i >= 0; i--) {
    if (Number(marketing[i]) > 0) {
      return i;
    }
  }
  return marketing.length - 1;
}

/** First results month index with a Twelve draw, or -1. */
export function resolveFirstDrawMonth(state, numCohorts) {
  for (let i = 0; i < numCohorts; i += 1) {
    if (resolveCohortDraw(state, i) > 0) {
      return i + 1;
    }
  }
  return -1;
}

export function buildFacilityFeePaymentSchedule(
  facilityFeeAmount,
  facilityFeePaymentMonth,
  installmentCount
) {
  const schedule = {};
  if (
    facilityFeeAmount <= 0 ||
    facilityFeePaymentMonth < 0 ||
    installmentCount <= 0
  ) {
    return schedule;
  }
  const count = Math.max(1, Math.floor(installmentCount));
  const baseShare = Math.floor(facilityFeeAmount / count);
  let remainder = facilityFeeAmount - baseShare * count;
  for (let i = 0; i < count; i += 1) {
    const month = facilityFeePaymentMonth + i;
    let amount = baseShare;
    if (i === count - 1) {
      amount += remainder;
    }
    if (amount > 0) {
      schedule[month] = amount;
    }
  }
  return schedule;
}

export function resolveFacilityFeeContext(state, numCohorts, gracePeriod) {
  const firstDrawMonth = resolveFirstDrawMonth(state, numCohorts);
  const facilityFeeAmount = resolveFacilityFeeAmount(state);
  const repaymentGrace = Math.max(0, Number(gracePeriod) || 0);
  const facilityFeeInstallments = Math.max(
    1,
    Math.min(
      MAX_FACILITY_FEE_INSTALLMENTS,
      Math.floor(Number(state.facilityFeeInstallments ?? 1))
    )
  );
  const facilityFeePaymentMonth =
    state?.facilityFeeFirstPaymentMonth != null &&
    state.facilityFeeFirstPaymentMonth !== ''
      ? resolveFacilityFeeStartMonth(state)
      : firstDrawMonth >= 0
        ? firstDrawMonth + repaymentGrace
        : -1;
  const facilityFeePaymentSchedule = buildFacilityFeePaymentSchedule(
    facilityFeeAmount,
    facilityFeePaymentMonth,
    facilityFeeInstallments
  );
  const scheduleMonths = Object.keys(facilityFeePaymentSchedule).map(Number);
  const facilityFeeLastPaymentMonth =
    scheduleMonths.length > 0 ? Math.max(...scheduleMonths) : -1;
  return {
    firstDrawMonth,
    facilityFeeAmount,
    facilityFeePaymentMonth,
    facilityFeeInstallments,
    facilityFeePaymentSchedule,
    facilityFeeLastPaymentMonth,
  };
}

export function runCalc(state) {
  state = applyCommitmentToPlanArrays(state);
  const adjustRoasVector = makeRoasScenarioAdjuster(state.roasScenarioAdjustment);
  const adjustedRoas = adjustRoasVector(state.roas);
  if (adjustedRoas !== state.roas) {
    state = { ...state, roas: adjustedRoas };
  }
  const N = state.marketing.length;
  const hillModel = state.cohortSheet?.cohorts?.length
    ? fitHillRoasModel(state.cohortSheet)
    : null;
  const useHillRoas = resolveUseHillRoasForFunding(state, hillModel);
  const spendRoasVectors = useHillRoas
    ? buildHillSpendRoasVectors(hillModel, state.marketing, state).map(
        adjustRoasVector
      )
    : null;
  const V = resolveEffectiveRepaymentPeriods(state, hillModel, useHillRoas);
  const gracePeriod = Math.max(0, Number(state.gracePeriod) || 0);
  const repaymentLag = gracePeriod;
  const planStartYear = state.planStartYear ?? START_YEAR;
  const planStartMonth = resolvePlanStartMonth(state);
  const calendarAlignedCols = isResultsCalendarAligned(state);

  const facilityFeeContext = resolveFacilityFeeContext(state, N, gracePeriod);
  const monthlyFeePct = resolveMonthlyFeePctFromState(state);
  const effectiveRepaymentRoasVectors = resolveAllCohortRepaymentRoasVectors(
    state,
    N,
    spendRoasVectors,
    V,
    monthlyFeePct,
    gracePeriod
  );
  const cohortSpecs = buildCohortRepaymentSpecs(
    state,
    N,
    effectiveRepaymentRoasVectors,
    gracePeriod
  );
  const maxRepaymentMonth = estimateMaxRepaymentMonth(cohortSpecs);

  let pb = useHillRoas
    ? (() => {
        const refVector = predictRoasVectorPct(hillModel, 0);
        const idx = refVector.findIndex((value) => value >= 100);
        return idx >= 0 ? idx : refVector.length;
      })()
    : state.roas.findIndex((v) => v >= 100);
  if (pb < 0) pb = V;
  const lastMarketingIdx = lastMarketingMonthIndex(state.marketing);
  const horizonFromRoas = lastMarketingIdx + V;
  const N_OUT = Math.max(
    N + 1,
    horizonFromRoas,
    N + pb,
    N + V + repaymentLag,
    maxRepaymentMonth + 1,
    facilityFeeContext.facilityFeeLastPaymentMonth >= 0
      ? facilityFeeContext.facilityFeeLastPaymentMonth + 1
      : 0
  );
  const cols = Array.from({ length: N_OUT }, (_, i) =>
    resultMonthLabel(i, planStartYear, planStartMonth, calendarAlignedCols)
  );

  const cohortRevenue = Array.from({ length: N }, () => new Array(N_OUT).fill(0));
  for (let m = 0; m < N; m++) {
    const vectorPct = effectiveRepaymentRoasVectors[m] ?? state.roas;
    const marginal = marginalFromCumulativeVector(vectorPct);
    for (let k = 0; k < marginal.length; k++) {
      const t = m + k;
      if (t < N_OUT) {
        cohortRevenue[m][t] = Math.round(
          (state.marketing[m] * marginal[k]) / 100
        );
      }
    }
  }
  const cohortMarketing = state.marketing.slice(0, N);

  const revenueNewCohorts = new Array(N_OUT).fill(0);
  for (let t = 0; t < N_OUT; t++) {
    for (let m = 0; m < N; m++) revenueNewCohorts[t] += cohortRevenue[m][t];
  }

  const oldCohortRevenueRows = buildOldCohortRevenueRows(
    state.cohortSheet,
    N_OUT,
    cols,
    planStartYear,
    planStartMonth,
    calendarAlignedCols
  );
  const revenueOldCohorts = mapOldCohortRevenue(
    state.cohortSheet,
    N_OUT,
    planStartYear,
    planStartMonth,
    calendarAlignedCols
  );

  const revenue = revenueNewCohorts.map(
    (v, i) => v + (revenueOldCohorts[i] || 0)
  );

  const cogs = revenue.map((r) => (-r * (100 - state.gpm)) / 100);
  const grossProfit = revenue.map((r, i) => r + cogs[i]);

  const marketingGrace = Math.max(
    0,
    Math.min(1, Number(state.marketingGracePeriod ?? 1))
  );
  const directMarketing = new Array(N_OUT).fill(0);
  const lastCohortMarketingPayment = resolveLastCohortMarketingPayment(state);
  if (lastCohortMarketingPayment > 0) {
    directMarketing[0] += -lastCohortMarketingPayment;
  }
  for (let i = 0; i < N; i++) {
    const t = i + marketingGrace;
    if (t < N_OUT) {
      directMarketing[t] += -state.marketing[i];
    }
  }

  const contribution = grossProfit.map((v, i) => v + directMarketing[i]);
  const opexRow = new Array(N_OUT).fill(-state.opex);
  const ebitda = contribution.map((v, i) => v + opexRow[i]);

  const draw = new Array(N_OUT).fill(0);
  for (let i = 0; i < N; i += 1) {
    const t = i + 1;
    if (t < N_OUT) {
      draw[t] = resolveCohortDraw(state, i);
    }
  }

  const {
    firstDrawMonth,
    facilityFeeAmount,
  } = facilityFeeContext;

  const facFeeRow = new Array(N_OUT).fill(0);
  if (firstDrawMonth >= 0 && facilityFeeAmount > 0 && firstDrawMonth < N_OUT) {
    facFeeRow[firstDrawMonth] = facilityFeeAmount;
  }

  const {
    beginBal,
    endBal,
    xirrFee,
    repay,
    cohortRepayments,
    cohortFeeRepayments,
  } = simulateTwelveBalance({
    numCohorts: N,
    N_OUT,
    draw,
    facFeeRow,
    facilityFeeContext,
    monthlyFeePct,
    gracePeriod,
    cohortSpecs,
  });

  const cohortDraws = new Array(N).fill(0);
  for (let i = 0; i < N; i += 1) {
    cohortDraws[i] = resolveCohortDraw(state, i);
  }

  const cashEnd = new Array(N_OUT).fill(0);
  let cash = state.beginCash;
  for (let i = 0; i < N_OUT; i++) {
    cash += ebitda[i] + draw[i] + repay[i];
    cashEnd[i] = cash;
  }

  const beginCashRow = new Array(N_OUT).fill(0);
  beginCashRow[0] = state.beginCash;
  for (let i = 1; i < N_OUT; i++) {
    beginCashRow[i] = cashEnd[i - 1];
  }

  const twelveFinance = new Array(N_OUT).fill(0);
  for (let i = 0; i < N_OUT; i++) {
    twelveFinance[i] = draw[i] + repay[i];
  }

  const twelveCF = new Array(N_OUT).fill(0);
  for (let i = 0; i < N_OUT; i++) {
    twelveCF[i] = -draw[i] - repay[i];
  }

  const totalRevenue = revenue.reduce((a, b) => a + b, 0);
  const totalEbitda = ebitda.reduce((a, b) => a + b, 0);
  const peakTwelveBalance = Math.max(...endBal);
  const totalWithdrawal = draw.reduce((a, b) => a + b, 0);
  const totalRepayments = repay.reduce((sum, v) => sum + Math.abs(v), 0);

  let lastDrawMonth = -1;
  for (let i = N_OUT - 1; i >= 0; i--) {
    if (draw[i] > 0) {
      lastDrawMonth = i;
      break;
    }
  }

  let runoffAtLastWithdrawal = 0;
  if (lastDrawMonth >= 0) {
    for (let t = lastDrawMonth + 1; t < N_OUT; t++) {
      runoffAtLastWithdrawal += revenue[t];
    }
  }

  const endingCashAtLastWithdrawal =
    lastDrawMonth >= 0 ? cashEnd[lastDrawMonth] : 0;
  const totalIRR = calcIRR(twelveCF);
  const totalIRRRounded = Number.isFinite(totalIRR)
    ? Math.round(totalIRR * 100000) / 100000
    : NaN;

  const {
    runrate,
    runoff,
    runrateExposurePct,
    runoffExposurePct,
    cashExposurePct,
    runoffPlusCashExposurePct,
  } = calcTwelvePerspectiveMetrics({
    revenue,
    endBal,
    cashEnd,
    cohortRevenue,
    oldCohortRevenueRows,
    N_OUT,
  });

  const runoffExposureCohortCount = parseRunoffExposureCohortCount(
    state.runoffExposureCohortCount
  );
  const runoffCohortWindow =
    runoffExposureCohortCount != null
      ? calcRunoffByFundedCohortWindow({
          cohortRevenue,
          cohortDraws,
          marketing: cohortMarketing,
          oldCohortRevenueRows,
          cohortCount: runoffExposureCohortCount,
          N_OUT,
        })
      : null;
  const runoffCohortWindowExposurePct =
    runoffCohortWindow != null
      ? calcExposurePctByMonth(endBal, runoffCohortWindow)
      : null;

  const { drawViolations, endBalViolations } = computeFacilityLimitViolations({
    draw,
    endBal,
    maxExposure: resolveRequiredMaxExposure(state.maxExposure),
    maxMonthlyDraw: shouldApplyDrawLimits(state) ? state.maxMonthlyDraw : null,
    maxExposureByMonth: buildMaxExposureByOutputMonth(state, N_OUT),
    maxMonthlyDrawByMonth: buildMaxMonthlyDrawByOutputMonth(state, N_OUT),
    maxDrawIncreasePct: shouldApplyDrawLimits(state)
      ? state.maxDrawIncreasePct
      : null,
  });

  return {
    cols,
    N_OUT,
    revenue,
    revenueNewCohorts,
    revenueOldCohorts,
    oldCohortRevenueRows,
    cogs,
    grossProfit,
    directMarketing,
    contribution,
    opexRow,
    ebitda,
    draw,
    xirrFee,
    facFeeRow,
    repay,
    cohortRepayments,
    cohortRevenue,
    cohortMarketing,
    cohortDraws,
    beginBal,
    endBal,
    beginCashRow,
    beginCashTotal: state.beginCash,
    twelveFinance,
    cashEnd,
    twelveCF,
    runrate,
    runoff,
    runrateExposurePct,
    runoffExposurePct,
    cashExposurePct,
    runoffPlusCashExposurePct,
    runoffExposureCohortCount,
    runoffCohortWindow,
    runoffCohortWindowExposurePct,
    drawViolations,
    endBalViolations,
    firstDrawMonth,
    lastDrawMonth,
    facilityFeeAmount,
    facilityFeeInstallments: facilityFeeContext.facilityFeeInstallments,
    facilityFeePaymentMonth: facilityFeeContext.facilityFeePaymentMonth,
    facilityFeePaymentSchedule: facilityFeeContext.facilityFeePaymentSchedule,
    facilityFeeLastPaymentMonth: facilityFeeContext.facilityFeeLastPaymentMonth,
    totalRevenue,
    totalEbitda,
    peakTwelveBalance,
    totalWithdrawal,
    totalRepayments,
    runoffAtLastWithdrawal,
    endingCashAtLastWithdrawal,
    totalIRR: totalIRRRounded,
    monthlyFeePct,
    useHillRoas,
    hillModel,
    spendRoasVectors,
    effectiveRepaymentRoasVectors,
    marketingGrace,
  };
}
